import { useState } from 'react'
import { Collapse, Dropdown } from 'react-bootstrap'
import { IResourceCategory } from '../../../types/resourceCategory'

interface ICategoryTreeNodeProps {
  category: IResourceCategory
  childrenMap: Map<string | null, IResourceCategory[]>
  selectedCategoryId: string | null
  onSelectCategory: (categoryId: string | null) => void
  onAddSubfolder: (parentId: string) => void
  onEditCategory: (category: IResourceCategory) => void
  onDeleteCategory: (category: IResourceCategory) => void
  onDropResources?: (categoryId: string | null, resourceIds: string[]) => void
}

export const CategoryTreeNode: React.FC<ICategoryTreeNodeProps> = ({
  category,
  childrenMap,
  selectedCategoryId,
  onSelectCategory,
  onAddSubfolder,
  onEditCategory,
  onDeleteCategory,
  onDropResources,
}) => {
  const children = childrenMap.get(category.id) || []
  const hasChildren = children.length > 0
  const isSelected = selectedCategoryId === category.id
  const [isExpanded, setIsExpanded] = useState(true)
  const [isDragOver, setIsDragOver] = useState(false)

  return (
    <div>
      <div
        className={`d-flex align-items-center gap-1 rounded px-2 py-1 category-tree-node ${
          isSelected ? 'bg-primary bg-opacity-10' : ''
        } ${isDragOver ? 'bg-warning bg-opacity-25 border border-warning' : ''}`}
        style={{ cursor: 'pointer' }}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes('application/x-resource-ids')) {
            e.preventDefault()
            e.stopPropagation()
            setIsDragOver(true)
          }
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setIsDragOver(false)
          const raw = e.dataTransfer.getData('application/x-resource-ids')
          if (raw) onDropResources?.(category.id, JSON.parse(raw))
        }}
      >
        {hasChildren ? (
          <button
            type="button"
            className="btn btn-sm p-0 border-0 text-muted"
            style={{ width: 16 }}
            onClick={(e) => {
              e.stopPropagation()
              setIsExpanded(!isExpanded)
            }}
          >
            <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`} style={{ fontSize: 11 }} />
          </button>
        ) : (
          <span style={{ width: 16 }} />
        )}

        <div
          className="d-flex align-items-center gap-2 flex-fill text-truncate"
          onClick={() => onSelectCategory(isSelected ? null : category.id)}
          title={category.name}
        >
          <i className={`bi ${isExpanded && hasChildren ? 'bi-folder2-open' : 'bi-folder'} ${isSelected ? 'text-primary' : 'text-warning'}`} />
          <span className={`text-truncate ${isSelected ? 'fw-bold text-primary' : ''}`}>
            {category.name}
          </span>
          {category.resourcesCount > 0 && (
            <span className="badge bg-light text-muted ms-auto">{category.resourcesCount}</span>
          )}
        </div>

        <span onClick={(e) => e.stopPropagation()}>
          <Dropdown align="end">
            <Dropdown.Toggle
              as="button"
              type="button"
              className="btn btn-sm p-0 border-0 text-muted category-node-menu"
              bsPrefix="no-caret"
            >
              <i className="bi bi-three-dots-vertical" style={{ fontSize: 12 }} />
            </Dropdown.Toggle>
            {/* strategy fixed: el menú escapa del overflow-y:auto del árbol */}
            <Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
              <Dropdown.Item onClick={() => onAddSubfolder(category.id)}>
                <i className="bi bi-folder-plus me-2" /> New subfolder
              </Dropdown.Item>
              <Dropdown.Item onClick={() => onEditCategory(category)}>
                <i className="bi bi-pencil me-2" /> Rename / Move
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item className="text-danger" onClick={() => onDeleteCategory(category)}>
                <i className="bi bi-trash me-2" /> Delete
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </span>
      </div>

      {hasChildren && (
        <Collapse in={isExpanded}>
          {/* Hijos indentados con línea guía vertical, alineada bajo el ícono de carpeta */}
          <div style={{ marginLeft: 19, borderLeft: '1px solid var(--bs-gray-300, #dbdfe9)', paddingLeft: 6 }}>
            {children.map((child) => (
              <CategoryTreeNode
                key={child.id}
                category={child}
                childrenMap={childrenMap}
                selectedCategoryId={selectedCategoryId}
                onSelectCategory={onSelectCategory}
                onAddSubfolder={onAddSubfolder}
                onEditCategory={onEditCategory}
                onDeleteCategory={onDeleteCategory}
                onDropResources={onDropResources}
              />
            ))}
          </div>
        </Collapse>
      )}
    </div>
  )
}
